import React, { useState, useCallback, useMemo, useEffect, forwardRef, cloneElement, Children } from 'react';

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var defaultRender = function defaultRender(_ref) {
  var children = _ref.children;
  return React.createElement(React.Fragment, null, children);
};

var useNodeRef = function useNodeRef() {
  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      domNode = _useState2[0],
      setDomNode = _useState2[1];

  var ref = useCallback(function (node) {
    if (node != null) {
      setDomNode(node);
    }
  }, []);
  return [domNode, ref];
};

var DEFAULT_TRANSITION_STATE = {
  enter: false,
  entering: false,
  entered: false,
  leave: false,
  leaving: false,
  left: false
};

var useTransitionState = function useTransitionState(initialState, runningState, doneState, duration) {
  // eslint-disable-next-line no-underscore-dangle
  var _initialState = useMemo(function () {
    return Object.assign({}, DEFAULT_TRANSITION_STATE, initialState);
  }, []);

  var _useState = useState(_initialState),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  useEffect(function () {
    var t1 = setTimeout(function () {
      setState(Object.assign({}, DEFAULT_TRANSITION_STATE, runningState));
    }, 10);
    var t2 = setTimeout(function () {
      setState(Object.assign({}, DEFAULT_TRANSITION_STATE, doneState));
    }, duration);
    return function () {
      // clear the timeouts when the component is destroyed
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [duration]);
  return state;
};

var Enter = forwardRef(function (props, ref) {
  var children = props.children,
      Wrapper = props.component,
      duration = props.duration,
      RenderTransition = props.render;
  var state = useTransitionState({
    enter: true
  }, {
    entering: true
  }, {
    entered: true
  }, duration);
  return React.createElement(RenderTransition, Object.assign({
    duration: duration
  }, state), React.createElement(Wrapper, {
    ref: ref
  }, children));
});

var Leave = function Leave(props) {
  var Wrapper = props.component,
      duration = props.duration,
      RenderTransition = props.render,
      html = props.html;
  var state = useTransitionState({
    leave: true
  }, {
    leaving: true
  }, {
    left: true
  }, duration);
  return React.createElement(RenderTransition, Object.assign({
    duration: duration
  }, state), React.createElement(Wrapper, {
    dangerouslySetInnerHTML: {
      __html: html
    }
  }));
};

var Element = function Element(props) {
  var children = props.children,
      component = props.component,
      duration = props.duration,
      enter = props.enter,
      leave = props.leave,
      render = props.render;

  var _useNodeRef = useNodeRef(),
      _useNodeRef2 = _slicedToArray(_useNodeRef, 2),
      node = _useNodeRef2[0],
      ref = _useNodeRef2[1];

  if (leave) {
    var html = node.innerHTML;
    return React.createElement(Leave, {
      component: component,
      duration: duration,
      render: render,
      html: html
    });
  }

  return React.createElement(Enter, {
    component: component,
    duration: duration,
    render: render,
    ref: ref
  }, children);
};

var getKeys = function getKeys(nodes) {
  return React.Children.map(nodes, function (child) {
    return child.key;
  });
};

var removeNode = function removeNode(nodes, childKeys, component, duration, render, key, i) {
  var node = nodes[i]; // if the node doesn't exist in children but exists in nodes
  // then it's been removed from the dom

  if (childKeys.indexOf(key) >= 0) {
    return false;
  } // if it's already being removed we can skip it


  if (node.props.leave) {
    return false;
  } // if it's currently entering, lift up the inner element


  if (node.props.enter) {
    // @ts-ignore
    node = node.props.children;
  } // wrap the node in a <Leave> that handles the transition state


  nodes[i] = React.createElement(Element, {
    key: key,
    component: component,
    duration: duration,
    render: render,
    enter: false,
    leave: true
  }, node);
  return true;
};

var addNode = function addNode(nodes, children, nodeKeys, component, duration, render, key, x) {
  var child = children[x];
  var i = nodeKeys.indexOf(key); // if we already have this node we can skip it

  if (i >= 0) {
    if (!nodes[i].props.leave) {
      return false;
    } // if the node is currently transitioning out, we should help it
    // along and then treat it as a new node


    nodes.splice(i, 1);
  }

  nodes.splice(x, 0, React.createElement(Element, {
    key: key,
    component: component,
    duration: duration,
    render: render,
    enter: true,
    leave: false
  }, child));
  return true;
};

var updateNode = function updateNode(nodes, children, nodeKeys, key, x) {
  var child = children[x];
  var i = nodeKeys.indexOf(key);
  var node = nodes[i]; // only do stuff when this node already exists

  if (i < 0) {
    return false;
  } // check if the child has changed


  if (node.props.children !== child) {
    // we want to clone the existing element and just replace the children prop
    nodes[i] = cloneElement(node, Object.assign({}, node.props, {
      children: child
    }), child);
    return true;
  }

  return false;
};

var useUpdateNodes = function useUpdateNodes(nodes, children, update, component, duration, render) {
  // we use keys to match up the latest children to the previous nodes
  var nodeKeys = useMemo(function () {
    return getKeys(nodes);
  }, [nodes]);
  var childKeys = useMemo(function () {
    return getKeys(children);
  }, [children]);
  var shouldUpdate = false; // we calculate the new nodes inside a useMemo so we don't do it every single render
  // we have a dirty let above so we can work out whether to update our nodes, even if
  // useMemo just returns the cached value

  var newNodes = useMemo(function () {
    // we're going to create a fresh nodes array and then mutate it as we do our checks
    var newNodes = nodes.slice();
    var anyRemoved = nodeKeys.reduce(function (flag, key, i) {
      var removed = removeNode(newNodes, childKeys, component, duration, render, key, i);
      return removed || flag;
    }, false);
    var anyUpdated = childKeys.reduce(function (flag, key, i) {
      var added = addNode(newNodes, children, nodeKeys, component, duration, render, key, i); // only run the update check if we haven't just added the node

      var updated = added ? false : updateNode(newNodes, children, nodeKeys, key, i);
      return added || updated || flag;
    }, false);
    shouldUpdate = anyRemoved || anyUpdated || shouldUpdate;
    return newNodes;
  }, [children]); // normally we would do this inside a useEffect but we need to do it immediately
  // otherwise we might attempt to render stuff whose internal state is messed up

  if (shouldUpdate) {
    update(newNodes);
  }
};

var isNodeNotLeaving = function isNodeNotLeaving(node) {
  return !node.props.leave;
};

var useCleanupTransitions = function useCleanupTransitions(nodes, update, duration) {
  // once any removed nodes have finished animating
  // we need to actually remove them from the dom
  useEffect(function () {
    var t = setTimeout(function () {
      var newNodes = nodes.filter(isNodeNotLeaving);

      if (newNodes.length !== nodes.length) {
        update(newNodes);
      }
    }, duration);
    return function () {
      clearTimeout(t);
    };
  }, [nodes]);
};

var Transition = function Transition(props) {
  var _props$duration = props.duration,
      duration = _props$duration === void 0 ? 250 : _props$duration,
      _props$render = props.render,
      render = _props$render === void 0 ? defaultRender : _props$render,
      _props$component = props.component,
      component = _props$component === void 0 ? 'span' : _props$component,
      children = props.children;
  var childrenArr = useMemo(function () {
    return Children.toArray(children);
  }, [children]);

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      nodes = _useState2[0],
      setNodes = _useState2[1];

  useUpdateNodes(nodes, childrenArr, setNodes, component, duration, render);
  useCleanupTransitions(nodes, setNodes, duration);
  return nodes;
};

export default Transition;
